// Hotspots tab on the setup screen: add a hotspot by clicking in the live tour, then fill in its card.
// Changes show in the preview straight away; they're saved with the rest of the setup.
import { draftUrl, uploadFile, hotspotToManifest, type HotspotRow, type HotspotKind, type Project } from '../data';
import { html, raw, icon, toast, confirmDialog, $, $$ } from '../ui';

export const KINDS: Array<{ id: HotspotKind; label: string; note: string; svg: string }> = [
  { id: 'info', label: 'Info', note: 'A title and a few words', svg: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"><circle cx="12" cy="12" r="9" stroke-width="1.8"/><path d="M12 11v5"/><circle cx="12" cy="8" r=".5" fill="currentColor"/></svg>' },
  { id: 'image', label: 'Gallery', note: 'Pictures to flick through', svg: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"><rect x="3.5" y="5" width="17" height="14" rx="2.5"/><circle cx="9" cy="10" r="1.6"/><path d="M4 17l5-4.5 4 3.5 3-2.5 4 3.5"/></svg>' },
  { id: 'video', label: 'Video', note: 'A YouTube or Vimeo video', svg: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="3"/><path d="M10 9.5l5 2.5-5 2.5z" fill="currentColor"/></svg>' },
  { id: 'product', label: 'Product', note: 'Supplier, price and a link', svg: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"><path d="M3.5 12.5V4h8.5l8.5 8.5-8 8z"/><circle cx="8" cy="8.5" r="1.4"/></svg>' },
  { id: 'link', label: 'Link', note: 'A button to any website', svg: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 14l7-7"/><path d="M12 7h5v5"/><path d="M17 15v3.5a1.5 1.5 0 0 1-1.5 1.5h-10A1.5 1.5 0 0 1 4 18.5v-10A1.5 1.5 0 0 1 5.5 7H9"/></svg>' },
];
const kindOf = (k: HotspotKind) => KINDS.find((x) => x.id === k)!;

async function shrinkToJpeg(file: File, maxW: number): Promise<Blob> {
  const bmp = await createImageBitmap(file);
  const scale = Math.min(1, maxW / bmp.width);
  const c = document.createElement('canvas'); c.width = Math.round(bmp.width * scale); c.height = Math.round(bmp.height * scale);
  c.getContext('2d')!.drawImage(bmp, 0, 0, c.width, c.height);
  return new Promise((res, rej) => c.toBlob((b) => (b ? res(b) : rej(new Error('Couldn’t read that picture'))), 'image/jpeg', 0.85));
}
const fixUrl = (u: string) => { const t = u.trim(); return !t ? '' : /^https?:\/\//i.test(t) ? t : `https://${t}`; };

export interface HotspotPanel {
  /** The tour preview finished placing (the studio clicked a spot). */
  placed(position: [number, number, number], normal?: [number, number, number]): void;
  /** Someone opened a hotspot in the preview: select it here. */
  opened(id: string): void;
  /** Re-send every hotspot to the preview (after it loads). */
  push(): void;
}

export function hotspotsPanel(host: HTMLElement, project: Project, rows: HotspotRow[], send: (msg: object) => void, touch: () => void): HotspotPanel {
  let openId: string | null = null;
  let pending: { kind: HotspotKind; moveId?: string } | null = null; // waiting for a click in the preview

  const push = () => send({ avr: 'hotspots', list: rows.map((r) => hotspotToManifest(r, (k) => draftUrl(project.id, k))) });
  const changed = () => { touch(); push(); };

  const draw = () => {
    host.innerHTML = html`
      <p class="muted">Add notes, pictures, videos, products and links to spots in the tour. Pick a type, then click where it goes in the preview.</p>
      <div class="hs-kinds">${KINDS.map((k) => html`<button class="hs-kind" data-add="${k.id}" title="${k.note}">${raw(k.svg)}<span>${k.label}</span></button>`)}</div>
      ${pending ? html`<div class="hs-placing"><span>${pending.moveId ? 'Click the new spot in the preview' : `Click in the preview where the ${kindOf(pending.kind).label.toLowerCase()} hotspot goes`}</span><button class="link-btn" id="hs-cancel">Cancel</button></div>` : ''}
      <div class="hs-list">${rows.length ? rows.map((r) => {
        const k = kindOf(r.kind); const on = r.id === openId;
        return html`<div class="hs-item ${on ? 'open' : ''}" data-id="${r.id}">
          <button class="hs-head" data-toggle="${r.id}"><span class="hs-ic">${raw(k.svg)}</span><span class="hs-t"><strong>${r.title || 'Untitled'}</strong><em>${k.label}</em></span>${icon(on ? 'up' : 'down', 15)}</button>
          ${on ? editor(r) : ''}
        </div>`;
      }) : html`<p class="muted small hs-none">No hotspots yet.</p>`}</div>`.html;
    wire();
  };

  const editor = (r: HotspotRow) => html`<div class="hs-edit">
    <label class="field"><span>Title</span><input data-f="title" value="${r.title}" maxlength="80"></label>
    ${r.kind === 'product' ? html`<div class="two-in">
      <label class="field"><span>Supplier or brand</span><input data-f="supplier" value="${r.supplier ?? ''}" placeholder="e.g. Caesarstone"></label>
      <label class="field"><span>Price</span><input data-f="price" value="${r.price ?? ''}" placeholder="e.g. From $1,200"></label></div>` : ''}
    <label class="field"><span>Text</span><textarea data-f="body" rows="3" maxlength="600" placeholder="${r.kind === 'product' ? 'Finish, size, colour…' : 'A few words for buyers'}">${r.body ?? ''}</textarea></label>
    ${r.kind === 'video' ? html`<label class="field"><span>Video link</span><input data-f="video_url" value="${r.video_url ?? ''}" placeholder="YouTube or Vimeo link"></label>` : html`
      <div class="field"><span>Pictures${r.kind === 'image' ? '' : html` <em class="muted">optional</em>`}</span>
        <div class="hs-pics">${r.image_keys.map((k, i) => html`<div class="hs-pic"><img src="${draftUrl(project.id, k)}" alt=""><button class="x" data-rm="${i}" aria-label="Remove picture">${icon('close', 11)}</button></div>`)}
          <label class="hs-pic add">${icon('plus', 18)}<input type="file" accept="image/*" multiple hidden data-pics></label></div></div>`}
    ${r.kind === 'product' || r.kind === 'link' ? html`<div class="two-in">
      <label class="field"><span>Button text</span><input data-f="link_label" value="${r.link_label ?? ''}" placeholder="${r.kind === 'product' ? 'View product' : 'Find out more'}"></label>
      <label class="field"><span>Web address</span><input data-f="link_url" value="${r.link_url ?? ''}" placeholder="https://"></label></div>` : ''}
    ${r.nx != null ? html`<label class="field"><span>Leader line <b data-leader-v>${r.leader > 0 ? `${r.leader.toFixed(1)} m` : 'Off'}</b></span>
      <input type="range" min="0" max="2" step="0.1" value="${r.leader}" data-leader>
      <em class="muted tiny">Moves the hotspot out from the surface, with a line back to the spot.</em></label>`
      : html`<p class="muted tiny">Move this hotspot to turn on a leader line.</p>`}
    <div class="hs-tools">
      <button class="btn small" data-show="${r.id}">${icon('eye', 14)}Show in preview</button>
      <button class="btn small" data-move="${r.id}">Move</button>
      <span class="spacer"></span>
      <button class="btn small ghost-bad" data-del="${r.id}">${icon('trash', 14)}Delete</button>
    </div>
  </div>`;

  const wire = () => {
    $$<HTMLButtonElement>('[data-add]', host).forEach((b) => (b.onclick = () => {
      pending = { kind: b.dataset.add as HotspotKind }; send({ avr: 'place' }); draw();
    }));
    const cancel = $('#hs-cancel', host);
    if (cancel) cancel.onclick = () => { pending = null; send({ avr: 'place-cancel' }); draw(); };
    $$<HTMLButtonElement>('[data-toggle]', host).forEach((b) => (b.onclick = () => {
      openId = openId === b.dataset.toggle ? null : b.dataset.toggle!;
      draw();
      if (openId) send({ avr: 'open-hotspot', id: openId });
    }));
    const r = rows.find((x) => x.id === openId);
    if (!r) return;
    const item = $(`.hs-item[data-id="${r.id}"]`, host);
    $$<HTMLInputElement | HTMLTextAreaElement>('[data-f]', item).forEach((inp) => {
      inp.oninput = () => {
        const f = inp.dataset.f as keyof HotspotRow;
        (r as unknown as Record<string, string | null>)[f] = inp.value || (f === 'title' ? '' : null);
        if (f === 'title') item.querySelector('.hs-t strong')!.textContent = inp.value || 'Untitled';
        changed();
      };
      if (inp.dataset.f === 'link_url') inp.onchange = () => { r.link_url = fixUrl(inp.value) || null; inp.value = r.link_url ?? ''; changed(); };
    });
    const leader = $<HTMLInputElement>('[data-leader]', item);
    if (leader) leader.oninput = () => {
      r.leader = Math.round(+leader.value * 10) / 10;
      $('[data-leader-v]', item).textContent = r.leader > 0 ? `${r.leader.toFixed(1)} m` : 'Off';
      changed();
    };
    $$<HTMLButtonElement>('[data-rm]', item).forEach((b) => (b.onclick = () => { r.image_keys.splice(+b.dataset.rm!, 1); changed(); draw(); }));
    const pics = $<HTMLInputElement>('[data-pics]', item);
    if (pics) pics.onchange = async () => {
      const files = [...(pics.files ?? [])].slice(0, 12);
      if (!files.length) return;
      toast(`Uploading ${files.length} picture${files.length === 1 ? '' : 's'}…`);
      try {
        for (const f of files) {
          const key = `hotspots/${r.id.slice(0, 8)}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}.jpg`;
          await uploadFile(project.id, key, await shrinkToJpeg(f, 1600));
          r.image_keys.push(key);
        }
        changed(); draw(); toast('Pictures added');
      } catch (e) { toast(e instanceof Error ? e.message : 'Upload failed', 'error', 7000); }
    };
    $(`[data-show="${r.id}"]`, item).onclick = () => send({ avr: 'open-hotspot', id: r.id });
    $(`[data-move="${r.id}"]`, item).onclick = () => { pending = { kind: r.kind, moveId: r.id }; send({ avr: 'place' }); draw(); };
    $(`[data-del="${r.id}"]`, item).onclick = async () => {
      if (!(await confirmDialog({ title: 'Delete this hotspot?', body: html`<p><b>${r.title || 'Untitled'}</b> comes off the tour when you save.</p>`, confirm: 'Delete', danger: true }))) return;
      rows.splice(rows.indexOf(r), 1); openId = null; changed(); draw();
    };
  };

  draw();
  return {
    placed(position, normal) {
      if (!pending) return;
      const [nx, ny, nz] = normal ?? [null, null, null];
      if (pending.moveId) {
        const r = rows.find((x) => x.id === pending!.moveId);
        if (r) { [r.x, r.y, r.z] = position; Object.assign(r, { nx, ny, nz }); }
        toast('Hotspot moved');
      } else {
        const id = crypto.randomUUID();
        rows.push({
          id, project_id: project.id, kind: pending.kind, x: position[0], y: position[1], z: position[2],
          title: `New ${kindOf(pending.kind).label.toLowerCase()}`, body: null, image_keys: [], video_url: null,
          supplier: null, price: null, link_label: null, link_url: null, lead_gate: false,
          nx, ny, nz, leader: 0,
        });
        openId = id;
      }
      pending = null;
      changed(); draw();
      setTimeout(() => $<HTMLInputElement>('.hs-item.open [data-f="title"]', host)?.select(), 50);
    },
    opened(id) { if (rows.some((r) => r.id === id) && openId !== id) { openId = id; draw(); } },
    push,
  };
}
